import { Typography } from "@mui/material";
import { HStack, tagColors } from "@zfp-akademie-a-s/zfp-design";
import { BlockedIcon, CheckCircleIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { InProgressIcon } from "Components/_Shared/Icons/Icons";
import { SearchedClientAnalysisState } from "Infrastructure/ApiZfp/Schema";
import { Resources, useResource } from "Infrastructure/Translations/Resources";

type Props = {
  readonly state: SearchedClientAnalysisState;
  readonly size?: "small" | "medium";
  readonly coloredText?: boolean;
};

export const AnalysisStatusLabel: React.FC<Props> = ({ state, size = "medium", coloredText = false }) => {
  const { t } = useResource();

  const iconSize = size === "small" ? 14 : 20;
  // the status chip's icon colours (tagColors), so the label and the chip match
  const colorMap = {
    [SearchedClientAnalysisState.in_progress]: tagColors.info.icon,
    [SearchedClientAnalysisState.completed]: tagColors.success.icon,
    [SearchedClientAnalysisState.declined]: tagColors.warning.icon,
  };

  const getText = (state: SearchedClientAnalysisState): string => {
    switch (state) {
      case SearchedClientAnalysisState.in_progress:
        return t(Resources.AnalysesListing.Status.InProgress);
      case SearchedClientAnalysisState.completed:
        return t(Resources.AnalysesListing.Status.Completed);
      case SearchedClientAnalysisState.declined:
        return t(Resources.AnalysesListing.Status.Declined);
      default:
        return "";
    }
  };

  const getColor = (state: SearchedClientAnalysisState): string | undefined => {
    switch (state) {
      case SearchedClientAnalysisState.in_progress:
      case SearchedClientAnalysisState.completed:
      case SearchedClientAnalysisState.declined:
        return colorMap[state];
      default:
        return undefined;
    }
  };

  const getIcon = (state: SearchedClientAnalysisState): React.ReactElement | null => {
    switch (state) {
      case SearchedClientAnalysisState.in_progress:
        return <InProgressIcon htmlColor={colorMap[state]} sx={{ fontSize: iconSize }} />;
      case SearchedClientAnalysisState.completed:
        return <CheckCircleIcon htmlColor={colorMap[state]} sx={{ fontSize: iconSize }} />;
      case SearchedClientAnalysisState.declined:
        return <BlockedIcon htmlColor={colorMap[state]} sx={{ fontSize: iconSize }} />;
      default:
        return null;
    }
  };

  return (
    <HStack spacing={1} alignItems="center" justifyContent={"center"}>
      {getIcon(state)}
      <Typography
        variant="body2"
        fontSize={size === "small" ? 12 : 14}
        lineHeight={"1.5"}
        sx={{ color: coloredText ? getColor(state) : "text.secondary" }}
      >
        {getText(state)}
      </Typography>
    </HStack>
  );
};
